import { BreakpointObserver } from "@angular/cdk/layout";
import { NgClass } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  computed,
  inject,
  output,
} from "@angular/core";
import { toPascalCase } from "@core/services/utils/functions/toPascalCase";
import { fixedNumberMoneyValue } from "@core/utils/fixed-number-money";
import { TranslateService } from "@ngx-translate/core";
import { Payment } from "@services/business/_models/interfaces/payment.interfaces";
import {
  RecordCustomActionEnum,
  RecordStatus,
} from "@services/business/_models/interfaces/record.interfaces";
import { ButtonComponent } from "@ui/controls/button/button.component";
import { Subject, takeUntil } from "rxjs";

@Component({
  selector: "record-status-compact",
  templateUrl: "./record-status-compact.component.html",
  styleUrl: "./record-status-compact.component.scss",
  imports: [NgClass, ButtonComponent],
})
export class RecordStatusCompactComponent implements OnInit, OnDestroy {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() public status: RecordStatus;
  @Input() public displayActions: { status: RecordStatus; label: string }[] =
    [];
  @Input() public paymentActions: Payment[] = [];

  @Output() public newStatus = new EventEmitter<RecordStatus>();
  @Output() public newPayment = new EventEmitter<Payment>();
  public customAction = output<RecordCustomActionEnum>();

  private readonly destroy$ = new Subject<void>();

  protected isMobile = false;

  private readonly statusConfig: Partial<
    Record<
      RecordStatus,
      {
        color: string;
        translationKey: string;
      }
    >
  > = {
    [RecordStatus.ENGAGED]: {
      color: "blue",
      translationKey: "RecordStatusComponent.EngagedHeader",
    },
    [RecordStatus.WAITING_FOR_PAYMENT]: {
      color: "blue",
      translationKey: "RecordStatusComponent.WaitingForPaymentHeader",
    },
    [RecordStatus.WAITING_FOR_SECOND_PAYMENT]: {
      color: "blue",
      translationKey: "RecordStatusComponent.WaitingForSecondPaymentHeader",
    },
    [RecordStatus.CREDIT_DOWNPAYMENT]: {
      color: "blue",
      translationKey: "RecordStatusComponent.CreditDownpaymentHeader",
    },
    [RecordStatus.WORK_AUTHORIZED]: {
      color: "blue",
      translationKey: "RecordStatusComponent.WorkAuthorizedHeader",
    },
    [RecordStatus.DEFINE_WORK]: {
      color: "blue",
      translationKey: "RecordStatusComponent.DefineWorkHeader",
    },
    [RecordStatus.WORK_DEFINED]: {
      color: "blue",
      translationKey: "RecordStatusComponent.WorkDefinedHeader",
    },
    [RecordStatus.PENDING_WORK_COMPLETION_CHECK]: {
      color: "blue",
      translationKey: "RecordStatusComponent.PendingWorkCompletionCheckHeader",
    },
    [RecordStatus.WORK_PENDING]: {
      color: "blue",
      translationKey: "RecordStatusComponent.WorkPendingHeader",
    },
    [RecordStatus.WORK_COMPLETED]: {
      color: "blue",
      translationKey: "RecordStatusComponent.WorkCompletedHeader",
    },
    [RecordStatus.DELIVERY_AUTHORIZED]: {
      color: "blue",
      translationKey: "RecordStatusComponent.DeliveryAuthorizedHeader",
    },
    [RecordStatus.RESULTS_DELIVERED]: {
      color: "green",
      translationKey: "RecordStatusComponent.ResultsDeliveredHeader",
    },
    [RecordStatus.CLOSED]: {
      color: "green",
      translationKey: "RecordStatusComponent.ClosedHeader",
    },
    [RecordStatus.EMAIL_PAYMENT_FAILED]: {
      color: "yellow",
      translationKey: `RecordStatus.${toPascalCase(RecordStatus.EMAIL_PAYMENT_FAILED)}`,
    },
  };

  ngOnInit() {
    this.breakpointObserver
      .observe(["(max-width: 48rem)"])
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isMobile = result.matches;
      });

    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected get buttonSize(): "xs" | "sm" {
    return this.isMobile ? "xs" : "sm";
  }

  private getStatusMatrix(status: RecordStatus) {
    const config = this.statusConfig[status];

    if (!config) {
      return {
        color: "blue",
        label: "",
      };
    }

    return {
      color: config.color,
      label: this.translateService.instant(config.translationKey),
    };
  }

  protected get statusLabel(): string {
    return this.getStatusMatrix(this.status)?.label || "";
  }

  protected get dotColor(): string {
    return this.getStatusMatrix(this.status)?.color || "blue";
  }

  getPaymentButtonText(amount?: number): string {
    if (!amount) {
      return this.translateService.instant("RecordStatusComponent.Pay");
    }

    const amountFormated = fixedNumberMoneyValue(amount);

    return this.translateService.instant("RecordStatusComponent.PayAmount", {
      amount: amountFormated,
    });
  }

  protected showCustomActions = computed(() => {
    return this.status === RecordStatus.EMAIL_PAYMENT_FAILED;
  });

  protected getCustomActionButtonText = computed(() => {
    if (this.status === RecordStatus.EMAIL_PAYMENT_FAILED) {
      return this.translateService.instant("RecordStatusComponent.FixEmail");
    } else {
      return "";
    }
  });

  protected emitCustomAction(): void {
    if (this.status === RecordStatus.EMAIL_PAYMENT_FAILED) {
      this.customAction.emit(RecordCustomActionEnum.FIX_EMAIL);
    }
  }

  emitStatus(status: RecordStatus): void {
    this.newStatus.emit(status);
  }

  emitPayment(payment: Payment): void {
    this.newPayment.emit(payment);
  }
}
